// ink-component
import React, { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import { transparentize } from 'polished';

import { modalTokens } from '../../tokens/components/Modal/tokens';
import { colorTokens } from '../../tokens/globals/tokens';
import Dialog from '../Dialog/Dialog';
import getGlobalProps from '../../helpers/constants/global-props';
import { BlockWrapper, BlockBodyWrapper } from '../Block/styles';

import { MOBILE_WIDTH } from '../../helpers/constants/responsive-breakpoints';

import ModalBody from './ModalBody/ModalBody';
import ModalFooter from './ModalFooter/ModalFooter';
import ModalHeader from './ModalHeader/ModalHeader';

/* We should check the possibility of adding the <dialog> html element to Modal instead of what we have today to be more semantically correct */

export interface ModalProps {
  /** Defines if the modal uses new modal styles and will be used with its subcomponents (`Modal.Header`, `Modal.Body`, or `Modal.Footer`) */
  enableNewStyles?: boolean;
  /** Modal content */
  children: ReactNode;
  /** Defines the width of the modal. Preset options include `mini`, `small`, `medium`, and `large`.
   * Alternatively, you can pass in a custom string such as `"200px"`, `"400px"`, etc */
  width?: string;
  /** Callback for the close event, either by clicking on backdrop or pressing Escape key. Consider disabling if an async operation is in progress. */
  onRequestClose?: (event: KeyboardEvent | MouseEvent) => void;
  /** Marks the element with a given id to make it easier to select on tests */
  'data-testid'?: string | number;
  /** @ignore */
  container?: HTMLElement; // to render in context other than body (i.e. in Primer's preview iframe)
}

// Grid based on 1280px
// - 56px (right margin)
// - 56px (left margin)
// - 32px * 11 gutters
// = 68px per column
const MODAL_MAX_WIDTH = Object.freeze({
  mini: 'calc((68px * 4) + (32px * 5))',
  small: 'calc((68px * 6) + (32px * 7))',
  medium: 'calc((68px * 8) + (32px * 9))',
  large: 'calc((68px * 10) + (32px * 11))',
});

const newStyles = css`
  background: ${modalTokens.ContainerBackgroundColorBaseDefault};
  border-radius: ${modalTokens.ContainerBorderRadiusBaseDefault};
  padding-top: ${modalTokens.ContainerSpacingTopBaseDefaultDesktop};
  padding-right: ${modalTokens.ContainerSpacingRightBaseDefaultDesktop};
  padding-bottom: ${modalTokens.ContainerSpacingBottomBaseDefaultDesktop};
  padding-left: ${modalTokens.ContainerSpacingLeftBaseDefaultDesktop};

  @media (max-width: ${MOBILE_WIDTH}px) {
    border-radius: ${modalTokens.ContainerBorderRadiusMobileDefault};
    display: flex;
    flex-direction: column;
    padding-top: ${modalTokens.ContainerSpacingTopBaseDefaultMobile};
    padding-right: ${modalTokens.ContainerSpacingRightBaseDefaultMobile};
    padding-bottom: ${modalTokens.ContainerSpacingBottomBaseDefaultMobile};
    padding-left: ${modalTokens.ContainerSpacingLeftBaseDefaultMobile};
    position: fixed;
    inset: 0;
  }
`;

const ModalWrapper = styled.div`
  margin: 64px auto;
  max-width: ${({ theme }) => (MODAL_MAX_WIDTH[theme.width] ? MODAL_MAX_WIDTH[theme.width] : theme.width)};

  ${({ theme }) => theme.enableNewStyles && newStyles};

  @media (max-width: ${MOBILE_WIDTH}px) {
    margin: 0px auto;
  }

  > ${BlockWrapper} {
    box-shadow: 0 1px 3px 0 ${transparentize(0.5, colorTokens.GlobalBrandBlack)};
    border-radius: ${modalTokens.ContainerBorderRadiusBaseDefault};

    @media (max-width: ${MOBILE_WIDTH}px) {
      border-radius: ${modalTokens.ContainerBorderRadiusMobileDefault};
      flex-direction: column;
      display: flex;
      margin: 0 auto;
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;

      > ${BlockBodyWrapper} {
        flex: 1;
        overflow: scroll;
      }
    }
  }

  // Legacy support
  > .ink-modal--content {
    width: 100%;
  }
`;

const globalProps = getGlobalProps({});

/**
 * @purpose Show content over the page in a restricted lightbox context
 * @group Modal
 * @categories layout
 * @aka dialog
 */
const Modal = ({
  onRequestClose,
  container,
  enableNewStyles,
  'data-testid': dataTestId,
  width,
  children,
}: ModalProps) => {
  // Set width and default widths for new and old modal styles
  const newWidth = width || (enableNewStyles ? 'mini' : 'medium');

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        isModal: true,
      })}
    >
      <Dialog onRequestClose={onRequestClose} container={container}>
        <ModalWrapper aria-modal theme={{ width: newWidth, enableNewStyles }} data-testid={dataTestId} {...globalProps}>
          {children}
        </ModalWrapper>
      </Dialog>
    </ThemeProvider>
  );
};

Modal.defaultProps = {
  onRequestClose: () => {},
  container: undefined,
};

Modal.displayName = 'Modal';

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export { ModalHeaderProps } from './ModalHeader/ModalHeader';
export { ModalBodyProps } from './ModalBody/ModalBody';
export { ModalFooterProps } from './ModalFooter/ModalFooter';

export default Modal;
